// Reconciliation — bookkeeper spreadsheet view.
//
// Cross-references Gilda's vendor side against its client side per
// final invoice so the bookkeeper can match both against the books.
// (The QuickBooks integration was removed 2026-10-17.)
//
// One row per Final Invoice. Each row shows the vendor-side state (PO
// ref + vendor, PO amount, actual vendor payment, vendor balance) next
// to the client-side state (proposal ref, FIN ref, FIN amount, sales
// tax, client balance), plus reconciliation columns (client paid date,
// status).
//
// Color rules (per BRAND language, no shading, no semitransparent fills):
//   - $0 balances: muted gray (#888880)
//   - Vendor balance > 0 with only deposit paid: muted gray + "(dep)" — structural pre-final state, no action
//   - Vendor balance > 0 with Final/Full vendor payment recorded yet still short: #D4007A fuchsia — actuals undershot
//   - Client balance > 0 with status='sent' (no client payment yet): muted gray — normal pipeline state
//   - Client balance > 0 with status='partial' (payment came in short): #D4007A fuchsia — needs follow-up
//   - Status text: paid #1A5C38, partial #D4007A, sent #111111, draft #888880
//
// Brand button geometry (matches CLAUDE.md design language):
//   - Project filter pills: 32px height, 14px horizontal padding, 16px radius, 12px font weight 500
//   - Sort dropdown: 24px height (column-level controls)
//   - Hairline borders 0.5px #E0E0DC / #D8D7D2; active 1.5px #111111
//   - White ground (#FFFFFF); black text (#111111) for primary, #888880 for muted

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import {
  fmtMoney, hasAdminAccess,
  compareReconciliationRows, computeReconciliationTotals,
  vendorPaymentReceiptMatch, formatVendorPaymentReference,
  buildReconciliationRows, RECON_FEE_CATEGORIES,
} from '../utils';
import { jumpTo } from '../lib/jump';
import CardStatementsSection from './CardStatementsSection';
import PoLedgerSection from './PoLedgerSection';

// ── Constants ────────────────────────────────────────────────────────

const COLOR_TEXT       = '#111111';
const COLOR_MUTED      = '#888880';
const COLOR_BORDER     = '#E0E0DC';
const COLOR_BORDER_DK  = '#D8D7D2';
const COLOR_ALERT      = '#D4007A';
const COLOR_PAID       = '#1A5C38';
const COLOR_GROUND     = '#FFFFFF';
// Dan's gross-profit pair (Cimon 2026-07-12): Full Payment (pre-tax
// client price) and Items + Fees (COGS) render in fuchsia text —
// header, body cells, totals — the two numbers he subtracts. Markup
// (the pre-computed difference), Taxes (pass-through), and Client
// Balance (collection status) stay in plain ink deliberately.
// Colored text on white ground per the no-fills rule.
const COLOR_CALC       = '#D4007A';

// Default sort direction per column key. Numeric/date columns default to
// desc (newest first / largest first feels right for money); text columns
// default to asc (A→Z). Header clicks consult this when switching to a
// new column; clicking the active column toggles direction.
const SORT_DEFAULT_DIR = {
  date:           'desc',
  amount:         'desc',
  po_amount:      'desc',
  vendor_paid:    'desc',
  vendor_balance: 'desc',
  client_balance: 'desc',
  items_fees:     'desc',
  markup:         'desc',
  taxes:          'desc',
  paid_date:      'desc',
  project:        'asc',
  vendor:         'asc',
  po:             'asc',
  fin:            'asc',
  status:         'asc',
};

// ── Helpers (inline, scoped to this component) ───────────────────────

function statusText(status) {
  if (status === 'paid')    return { text: 'Paid',    color: COLOR_PAID };
  if (status === 'partial') return { text: 'Partial', color: COLOR_ALERT };
  if (status === 'sent')    return { text: 'Sent',    color: COLOR_TEXT };
  if (status === 'draft')   return { text: 'Draft',   color: COLOR_MUTED };
  return { text: status || '—', color: COLOR_MUTED };
}

function vendorBalanceRender(balance, hasFinalPayment) {
  if (balance <= 0.005) {
    return { text: fmtMoney(0), color: COLOR_MUTED };
  }
  if (!hasFinalPayment) {
    return { text: fmtMoney(balance) + ' (dep)', color: COLOR_MUTED };
  }
  return { text: fmtMoney(balance), color: COLOR_ALERT };
}

function clientBalanceRender(balance, status) {
  if (balance <= 0.005) {
    return { text: fmtMoney(0), color: COLOR_MUTED };
  }
  if (status === 'partial') {
    return { text: fmtMoney(balance), color: COLOR_ALERT };
  }
  return { text: fmtMoney(balance), color: COLOR_MUTED };
}

function fmtPaymentDate(iso) {
  if (!iso) return '';
  // payment_allocations.created_at is timestamptz; render as YYYY-MM-DD.
  return String(iso).slice(0, 10);
}

// Click-to-sort column header. Renders the label + a small ↑/↓ indicator
// when this column is the active sort key. Click toggles direction via
// the parent's toggleSort handler. The cursor is 'pointer' to signal
// affordance; the active column shows the text in COLOR_TEXT instead of
// the muted gray used for inactive columns.
function SortableTH(p) {
  const active = p.sortState && p.sortState.key === p.sortKey;
  const dir    = active ? p.sortState.dir : '';
  const arrow  = active ? (dir === 'asc' ? '\u2191' : '\u2193') : '';
  const style  = Object.assign({}, p.style, {
    cursor: 'pointer', userSelect: 'none',
    color: active ? '#111111' : (p.style && p.style.color) || '#888880',
  });
  return (
    <th
      style={style}
      onClick={function() { p.onClick(p.sortKey); }}
      title={p.title || ''}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {p.label}
      {active ? <span style={{ marginLeft: 4, color: '#111111' }}>{arrow}</span> : null}
      {p.sub ? (
        <div style={{ fontSize: 8, fontWeight: 500, letterSpacing: '0.07em', marginTop: 1 }}>
          {p.sub}
        </div>
      ) : null}
    </th>
  );
}

// ── Reconciliation drawer (Part B B1+B2, 2026-06-14) ──────────────────
//
// Right-side slide-in panel that opens when the bookkeeper clicks one of
// four cells: PO / Vendor, Total Vendor Paid, FIN Ref, Client Balance.
// Each kind renders a focused read-only view + a link-out button that
// jumps to the source surface via the detail-jump pattern. Inside the
// vendor-payments drawer, each payment row drills into its source
// receipt with a soft amount-match indicator (vendorPaymentReceiptMatch
// in utils.js — tolerance band max($0.50, payment × 0.5%)).
//
// Style: 480px fixed-right panel, opaque backdrop, click-outside +
// Escape close. JSX to match the file. Inline styles keep the drawer
// scoped here; if a future surface wants the same primitive, extract.

const DRAWER_STYLES = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 1100,
  },
  panel: {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
    background: COLOR_GROUND,
    boxShadow: '-2px 0 12px rgba(0,0,0,0.18)',
    zIndex: 1101, padding: 24,
    overflowY: 'auto',
    fontFamily: 'inherit',
    color: COLOR_TEXT,
  },
  closeBtn: {
    position: 'absolute', top: 14, right: 14,
    width: 28, height: 28, padding: 0,
    border: 'none', background: 'transparent',
    fontSize: 18, color: COLOR_MUTED, cursor: 'pointer',
    lineHeight: 1, fontFamily: 'inherit',
  },
  heading:  { fontSize: 14, fontWeight: 500, color: COLOR_TEXT, marginBottom: 4, marginTop: 0 },
  subhead:  { fontSize: 11, color: COLOR_MUTED, marginBottom: 18 },
  section:  { marginTop: 18, marginBottom: 14 },
  sectionH: {
    fontSize: 9, fontWeight: 500, color: COLOR_MUTED,
    letterSpacing: '0.07em', textTransform: 'uppercase',
    paddingBottom: 6, marginBottom: 8,
    borderBottom: '0.5px solid ' + COLOR_BORDER,
  },
  rowCard: {
    padding: '8px 0',
    borderBottom: '0.5px solid ' + COLOR_BORDER,
    fontSize: 11, color: COLOR_TEXT,
  },
  rowMeta: { fontSize: 10, color: COLOR_MUTED, marginTop: 2 },
  empty:   { fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic', padding: '12px 0' },
  primary: {
    display: 'inline-block', marginTop: 18,
    height: 32, padding: '0 16px', lineHeight: '32px',
    border: '1px solid ' + COLOR_TEXT, borderRadius: 4,
    background: COLOR_TEXT, color: '#FFFFFF',
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  drillBtn: {
    height: 24, padding: '0 10px',
    border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 4,
    background: COLOR_GROUND, color: COLOR_TEXT,
    fontSize: 10, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

// ── Fee itemization + markup split (2026-09-08) ───────────────────────
//
// Cimon's rule: itemize all fees, costs and markups and surface them so
// the money flow is knowable. Before this the Reconciliation table had
// zero mentions of freight, crating, tariff or installation — Dan could
// see COGS, Markup and Taxes as three lumps and nothing underneath them.
//
// These live in the DRAWER, not as new columns: the table already runs 14
// columns at 1480px and five more would break it. The drawer is also
// where the three numbers belong together — cost is what the vendor
// charged, billed is what the client paid, margin is the difference, and
// reconciling means reading them across, not down.
//
// Design: hairline rules and colored text on the white ground, no tinted
// fills. Margin gets the calc fuchsia because it is the derived number.

const FEE_LABELS = {
  freight:      'Freight',
  crating:      'Crating',
  installation: 'Installation',
  tariff:       'Tariff',
  salesTax:     'Sales tax (per item)',
  custom:       'Custom',
};

const FEE_TABLE_STYLES = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  th: {
    fontSize: 9, fontWeight: 500, color: COLOR_MUTED,
    letterSpacing: '0.07em', textTransform: 'uppercase',
    textAlign: 'right', padding: '0 0 6px',
    borderBottom: '0.5px solid ' + COLOR_BORDER,
  },
  td:     { padding: '7px 0', borderBottom: '0.5px solid ' + COLOR_BORDER, textAlign: 'right', color: COLOR_TEXT },
  total:  { padding: '8px 0', borderTop: '1px solid ' + COLOR_BORDER_DK, textAlign: 'right', fontWeight: 500 },
  note:   { fontSize: 10, color: COLOR_MUTED, marginTop: 8, lineHeight: 1.5 },
};

function renderFeeItemization(r) {
  const cats = r.feesByCategory || {};
  // Only categories that carry money. A row of zeros teaches nothing and
  // an empty section is more honest than six dashes.
  const live = RECON_FEE_CATEGORIES.filter(function(k) {
    const b = cats[k] || {};
    return (parseFloat(b.cost) || 0) !== 0 || (parseFloat(b.client) || 0) !== 0;
  });
  if (live.length === 0) return null;
  const sum = function(field) {
    return live.reduce(function(s, k) { return s + (parseFloat((cats[k] || {})[field]) || 0); }, 0);
  };
  const thLeft = Object.assign({}, FEE_TABLE_STYLES.th, { textAlign: 'left' });
  const tdLeft = Object.assign({}, FEE_TABLE_STYLES.td, { textAlign: 'left' });
  return (
    <div style={DRAWER_STYLES.section} data-testid="recon-fee-itemization">
      <div style={DRAWER_STYLES.sectionH}>Fees itemized</div>
      <table style={FEE_TABLE_STYLES.table}>
        <thead>
          <tr>
            <th style={thLeft}>Fee</th>
            <th style={FEE_TABLE_STYLES.th}>Cost</th>
            <th style={FEE_TABLE_STYLES.th}>Billed</th>
            <th style={Object.assign({}, FEE_TABLE_STYLES.th, { color: COLOR_CALC })}>Margin</th>
          </tr>
        </thead>
        <tbody>
          {live.map(function(k) {
            const b = cats[k] || {};
            return (
              <tr key={k}>
                <td style={tdLeft}>{FEE_LABELS[k] || k}</td>
                <td style={FEE_TABLE_STYLES.td}>{fmtMoney(parseFloat(b.cost) || 0)}</td>
                <td style={FEE_TABLE_STYLES.td}>{fmtMoney(parseFloat(b.client) || 0)}</td>
                <td style={Object.assign({}, FEE_TABLE_STYLES.td,
                  (parseFloat(b.markup) || 0) === 0 ? { color: COLOR_MUTED } : { color: COLOR_CALC })}>
                  {(parseFloat(b.markup) || 0) === 0 ? 'at cost' : fmtMoney(parseFloat(b.markup) || 0)}
                </td>
              </tr>
            );
          })}
          <tr>
            <td style={Object.assign({}, FEE_TABLE_STYLES.total, { textAlign: 'left' })}>Total</td>
            <td style={FEE_TABLE_STYLES.total}>{fmtMoney(sum('cost'))}</td>
            <td style={FEE_TABLE_STYLES.total}>{fmtMoney(sum('client'))}</td>
            <td style={Object.assign({}, FEE_TABLE_STYLES.total, { color: COLOR_CALC })}>{fmtMoney(sum('markup'))}</td>
          </tr>
        </tbody>
      </table>
      {(r.customFeeRows || []).length > 0 ? (
        <div style={FEE_TABLE_STYLES.note}>
          {r.customFeeRows.map(function(c, i) {
            return (
              <div key={i}>
                {c.label || '(unlabeled)'} &middot; {fmtMoney(c.cost)} at {c.markupPct}%
                {c.itemNumber ? ' · ' + c.itemNumber : ''}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function renderMarkupSplit(r) {
  const ms = r.markupSplit || {};
  const rows = [
    ['Product', ms.product],
    ['Fees', ms.fees],
    ['Card fee recovered', ms.ccFee],
    ['Adjustments', ms.adjustments],
  ].filter(function(pair) { return (parseFloat(pair[1]) || 0) !== 0; });
  if (rows.length === 0) return null;
  const tdLeft = Object.assign({}, FEE_TABLE_STYLES.td, { textAlign: 'left' });
  return (
    <div style={DRAWER_STYLES.section} data-testid="recon-markup-split">
      <div style={DRAWER_STYLES.sectionH}>Markup dollars</div>
      <table style={FEE_TABLE_STYLES.table}>
        <tbody>
          {rows.map(function(pair) {
            return (
              <tr key={pair[0]}>
                <td style={tdLeft}>{pair[0]}</td>
                <td style={FEE_TABLE_STYLES.td}>{fmtMoney(parseFloat(pair[1]) || 0)}</td>
              </tr>
            );
          })}
          <tr>
            <td style={Object.assign({}, FEE_TABLE_STYLES.total, { textAlign: 'left' })}>Total</td>
            <td style={Object.assign({}, FEE_TABLE_STYLES.total, { color: COLOR_CALC })}>
              {fmtMoney(parseFloat(ms.total) || 0)}
            </td>
          </tr>
        </tbody>
      </table>
      {/* Provenance, because a margin number that can silently restate
          itself is worse than one you distrust on purpose. Stored means
          the invoice recorded it at generation; derived means this row
          predates the columns and the figure moves if an item is edited. */}
      {r.markupSource === 'derived' ? (
        <div style={FEE_TABLE_STYLES.note}>
          Derived from current item data — this FIN predates stored markup, so editing an item moves it.
        </div>
      ) : null}
    </div>
  );
}

function ReconciliationDrawer(p) {
  const ctx = useAppContext();
  const items            = ctx.items            || [];
  const purchaseOrders   = ctx.purchaseOrders   || [];
  const vendorPayments   = ctx.vendorPayments   || [];
  const receipts         = ctx.receipts         || [];
  const paymentAllocations = ctx.paymentAllocations || [];
  const clientPayments   = ctx.clientPayments   || [];
  const invoices         = ctx.invoices         || [];

  // Close-on-Escape: attach a keydown listener when open, detach on
  // close. The dependency array re-binds when open/onClose change so
  // a closed drawer's listener doesn't fire.
  React.useEffect(function() {
    if (!p.open) return;
    function onKey(e) { if (e.key === 'Escape') p.onClose(); }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [p.open, p.onClose]);

  if (!p.open || !p.row) return null;
  const r = p.row;

  // ── Drawer body, branched by kind ───────────────────────────────────

  function renderPoBody() {
    const po = purchaseOrders.find(function(x) { return x && x.id === r.poId; });
    const poItems = (po && Array.isArray(po.items))
      ? po.items.map(function(iid) { return items.find(function(it) { return it && it.id === iid; }); }).filter(Boolean)
      : [];
    const matchedVPs = vendorPayments.filter(function(vp) {
      if (!vp || !po) return false;
      const t = vp.paymentType || '';
      if (t === 'Fee') return false;
      const vpPo = vp.poId || vp.po_id || '';
      if (vpPo && String(vpPo) === String(po.id)) return true;
      const ref = String(vp.reference || '');
      return po.po_ref && ref.indexOf(po.po_ref) >= 0;
    });
    return (
      <div>
        <h2 style={DRAWER_STYLES.heading}>{r.poRef}</h2>
        <div style={DRAWER_STYLES.subhead}>{r.vendorName} &middot; {r.project}</div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Items on PO ({poItems.length})</div>
          {poItems.length === 0 ? (
            <div style={DRAWER_STYLES.empty}>No items resolved on this PO.</div>
          ) : poItems.map(function(it) {
            return (
              <div key={it.id} style={DRAWER_STYLES.rowCard}>
                <div>{it.itemNumber || it.id.slice(0, 8)} &middot; {it.productName || '(unnamed)'}</div>
                <div style={DRAWER_STYLES.rowMeta}>
                  Status: {it.status || '—'} &middot; Net: {fmtMoney(parseFloat(it.netPrice) || 0)} &times; {parseFloat(it.qty) || 1}
                </div>
              </div>
            );
          })}
        </div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Payment timeline ({matchedVPs.length})</div>
          {matchedVPs.length === 0 ? (
            <div style={DRAWER_STYLES.empty}>No vendor payments recorded yet.</div>
          ) : matchedVPs.map(function(vp) {
            return (
              <div key={vp.id} style={DRAWER_STYLES.rowCard}>
                <div>{[fmtPaymentDate(vp.paymentDate), vp.paymentType, fmtMoney(parseFloat(vp.amount) || 0)].filter(Boolean).join(' \u00b7 ')}</div>
                <div style={DRAWER_STYLES.rowMeta}>{formatVendorPaymentReference(vp.reference) || '(no reference)'}</div>
              </div>
            );
          })}
        </div>

        {r.poId ? (
          <button type="button" style={DRAWER_STYLES.primary}
            onClick={function() { jumpTo('purchase-orders', r.poId); p.onClose(); }}>
            Open PO in Purchase Orders &rarr;
          </button>
        ) : null}
      </div>
    );
  }

  function renderVendorPaymentsBody() {
    const po = purchaseOrders.find(function(x) { return x && x.id === r.poId; });
    const matchedVPs = vendorPayments.filter(function(vp) {
      if (!vp || !po) return false;
      const vpPo = vp.poId || vp.po_id || '';
      if (vpPo && String(vpPo) === String(po.id)) return true;
      const ref = String(vp.reference || '');
      return po.po_ref && ref.indexOf(po.po_ref) >= 0;
    });
    return (
      <div>
        <h2 style={DRAWER_STYLES.heading}>Vendor Payments &middot; {r.poRef}</h2>
        <div style={DRAWER_STYLES.subhead}>
          {r.vendorName} &middot; Total {fmtMoney(r.actualVendorPayment)} across {matchedVPs.length} payment{matchedVPs.length === 1 ? '' : 's'}
        </div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Payment rows</div>
          {matchedVPs.length === 0 ? (
            <div style={DRAWER_STYLES.empty}>No vendor payments recorded yet.</div>
          ) : matchedVPs.map(function(vp) {
            const srcId  = vp.sourceReceiptId || vp.source_receipt_id || '';
            const isFee  = (vp.paymentType || '') === 'Fee';
            const rcpt   = srcId ? receipts.find(function(x) { return x && x.id === srcId; }) : null;
            // Amount-match: payment amount vs receipt totalAmount. Only
            // meaningful for principal rows (Deposit / Final / Full).
            // Fee rows (CC processing) carry the fee amount, not the
            // receipt total — comparing would always flag, hide the
            // indicator for those.
            const match = (!isFee && rcpt)
              ? vendorPaymentReceiptMatch(vp.amount, rcpt.totalAmount)
              : null;
            return (
              <div key={vp.id} style={DRAWER_STYLES.rowCard}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div>{[fmtPaymentDate(vp.paymentDate), vp.paymentType, fmtMoney(parseFloat(vp.amount) || 0)].filter(Boolean).join(' \u00b7 ')}</div>
                    <div style={DRAWER_STYLES.rowMeta}>{formatVendorPaymentReference(vp.reference) || '(no reference)'}</div>
                  </div>
                  {srcId ? (
                    <button type="button" style={DRAWER_STYLES.drillBtn}
                      onClick={function() { jumpTo('receipts', srcId); p.onClose(); }}>
                      Receipt &rarr;
                    </button>
                  ) : null}
                </div>
                {!srcId ? (
                  <div style={{ fontSize: 10, color: COLOR_MUTED, fontStyle: 'italic', marginTop: 4 }}>
                    Manual entry &mdash; no receipt attached.
                  </div>
                ) : !rcpt ? (
                  <div style={{ fontSize: 10, color: COLOR_MUTED, fontStyle: 'italic', marginTop: 4 }}>
                    Linked receipt not loaded in this session.
                  </div>
                ) : match && match.matches ? (
                  <div style={{ fontSize: 10, color: COLOR_PAID, marginTop: 4 }}>
                    &#x2713; Receipt amount matches ({fmtMoney(match.receiptAmount)}).
                  </div>
                ) : match ? (
                  // Mismatch hint: yellow (#F5C800) dot + neutral text.
                  // Soft signal only — never blocking. Fuchsia is reserved
                  // for quoting/scan-gate semantics in the app's palette
                  // and would mis-cue this as an alert. Yellow is the
                  // "specifying" stage color so reading next to specifying-
                  // stage rows is the open watch-item; can be moved to a
                  // neutral palette outside the pipeline if it conflicts.
                  <div style={{ fontSize: 10, color: COLOR_TEXT, marginTop: 4 }}>
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      background: '#F5C800', marginRight: 6, verticalAlign: 'middle',
                    }} />
                    Receipt total {fmtMoney(match.receiptAmount)} (delta {fmtMoney(match.delta)}, tolerance {fmtMoney(match.tolerance)})
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderFinBody() {
    const finItemIds = (function() {
      const inv = invoices.find(function(x) { return x && x.id === r.finId; });
      if (!inv) return [];
      return Array.isArray(inv.itemIds) ? inv.itemIds : (Array.isArray(inv.item_ids) ? inv.item_ids : []);
    })();
    const finItems = finItemIds
      .map(function(iid) { return items.find(function(it) { return it && it.id === iid; }); })
      .filter(Boolean);
    const finAllocs = paymentAllocations.filter(function(pa) {
      if (!pa) return false;
      const dt = pa.documentType || pa.document_type;
      const di = pa.documentId   || pa.document_id;
      return dt === 'invoice' && di === r.finId;
    });
    return (
      <div>
        <h2 style={DRAWER_STYLES.heading}>{r.finRef}</h2>
        <div style={DRAWER_STYLES.subhead}>
          {r.project} &middot; Full Payment {fmtMoney(r.finAmount)}
        </div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Bundled items ({finItems.length})</div>
          {finItems.length === 0 ? (
            <div style={DRAWER_STYLES.empty}>No items resolved on this FIN.</div>
          ) : finItems.map(function(it) {
            return (
              <div key={it.id} style={DRAWER_STYLES.rowCard}>
                <div>{it.itemNumber || it.id.slice(0, 8)} &middot; {it.productName || '(unnamed)'}</div>
                <div style={DRAWER_STYLES.rowMeta}>
                  Net: {fmtMoney(parseFloat(it.netPrice) || 0)} &times; {parseFloat(it.qty) || 1}
                </div>
              </div>
            );
          })}
        </div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Breakdown</div>
          <div style={DRAWER_STYLES.rowCard}>Items + Fees &middot; {fmtMoney(r.itemsAndFeesNet)}</div>
          <div style={DRAWER_STYLES.rowCard}>Markup &middot; {fmtMoney(r.markup)}</div>
          <div style={DRAWER_STYLES.rowCard}>Taxes &middot; {r.salesTax > 0 ? fmtMoney(r.salesTax) : '—'}</div>
        </div>

        {renderFeeItemization(r)}
        {renderMarkupSplit(r)}

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Client payment allocations ({finAllocs.length})</div>
          {finAllocs.length === 0 ? (
            <div style={DRAWER_STYLES.empty}>No payments recorded against this FIN yet.</div>
          ) : finAllocs.map(function(pa) {
            const pid = pa.paymentId || pa.payment_id;
            const cp  = pid ? clientPayments.find(function(x) { return x && x.id === pid; }) : null;
            const dt  = cp ? (cp.paymentDate || cp.payment_date) : (pa.createdAt || pa.created_at);
            return (
              <div key={pa.id} style={DRAWER_STYLES.rowCard}>
                <div>{fmtPaymentDate(dt)} &middot; {fmtMoney(parseFloat(pa.amount) || 0)}</div>
                <div style={DRAWER_STYLES.rowMeta}>
                  {cp ? (cp.method || '—') + (cp.bankReference ? ' · ' + cp.bankReference : '') : '(payment not loaded)'}
                </div>
              </div>
            );
          })}
        </div>

        {r.finId ? (
          <button type="button" style={DRAWER_STYLES.primary}
            onClick={function() { jumpTo('invoices', r.finId); p.onClose(); }}>
            Open FIN in Client Invoices &rarr;
          </button>
        ) : null}
      </div>
    );
  }

  function renderClientBalanceBody() {
    const inv = invoices.find(function(x) { return x && x.id === r.finId; });
    const amountDue = inv ? (parseFloat(inv.amountDue || inv.amount_due) || 0) : r.finAmount;
    const finAllocs = paymentAllocations.filter(function(pa) {
      if (!pa) return false;
      const dt = pa.documentType || pa.document_type;
      const di = pa.documentId   || pa.document_id;
      return dt === 'invoice' && di === r.finId;
    });
    const clientPaid = finAllocs.reduce(function(s, pa) { return s + (parseFloat(pa.amount) || 0); }, 0);
    const balance = Math.max(0, amountDue - clientPaid);
    return (
      <div>
        <h2 style={DRAWER_STYLES.heading}>Client Balance &middot; {r.finRef}</h2>
        <div style={DRAWER_STYLES.subhead}>{r.project} &middot; status {r.finStatus}</div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Summary</div>
          <div style={DRAWER_STYLES.rowCard}>Amount due &middot; {fmtMoney(amountDue)}</div>
          <div style={DRAWER_STYLES.rowCard}>Client paid &middot; {fmtMoney(clientPaid)}</div>
          <div style={Object.assign({}, DRAWER_STYLES.rowCard, {
            color: balance > 0.005 ? COLOR_ALERT : COLOR_PAID, fontWeight: 500,
          })}>
            Balance &middot; {fmtMoney(balance)}
          </div>
        </div>

        <div style={DRAWER_STYLES.section}>
          <div style={DRAWER_STYLES.sectionH}>Payment allocations ({finAllocs.length})</div>
          {finAllocs.length === 0 ? (
            <div style={DRAWER_STYLES.empty}>No payments recorded against this FIN yet.</div>
          ) : finAllocs.map(function(pa) {
            const pid = pa.paymentId || pa.payment_id;
            const cp  = pid ? clientPayments.find(function(x) { return x && x.id === pid; }) : null;
            const dt  = cp ? (cp.paymentDate || cp.payment_date) : (pa.createdAt || pa.created_at);
            return (
              <div key={pa.id} style={DRAWER_STYLES.rowCard}>
                <div>{fmtPaymentDate(dt)} &middot; {fmtMoney(parseFloat(pa.amount) || 0)}</div>
                <div style={DRAWER_STYLES.rowMeta}>
                  {cp ? (cp.method || '—') + (cp.bankReference ? ' · ' + cp.bankReference : '') : '(payment not loaded)'}
                </div>
              </div>
            );
          })}
        </div>

        {r.finId ? (
          <button type="button" style={DRAWER_STYLES.primary}
            onClick={function() { jumpTo('invoices', r.finId); p.onClose(); }}>
            Open FIN in Client Invoices &rarr;
          </button>
        ) : null}
      </div>
    );
  }

  let body = null;
  if      (p.kind === 'po')               body = renderPoBody();
  else if (p.kind === 'vendor-payments')  body = renderVendorPaymentsBody();
  else if (p.kind === 'fin')              body = renderFinBody();
  else if (p.kind === 'client-balance')   body = renderClientBalanceBody();

  return (
    <div>
      <div style={DRAWER_STYLES.backdrop} onClick={p.onClose} />
      <div style={DRAWER_STYLES.panel} role="dialog" aria-label="Reconciliation detail">
        <button type="button" style={DRAWER_STYLES.closeBtn} onClick={p.onClose} aria-label="Close">&times;</button>
        {body}
      </div>
    </div>
  );
}


// ── Main component ───────────────────────────────────────────────────

export default function ReconciliationSection(p) {
  const ctx = useAppContext();
  const userEmail = ctx.userEmail || '';
  const firmSettings = ctx.firmSettings || {};

  const invoices = ctx.invoices || [];
  const items = ctx.items || [];
  const purchaseOrders = ctx.purchaseOrders || [];
  const vendorPayments = ctx.vendorPayments || [];
  const paymentAllocations = ctx.paymentAllocations || [];
  const clientPayments = ctx.clientPayments || [];
  const proposals = ctx.proposals || [];
  const projects = ctx.projects || [];

  const [projFilter, setProjFilter] = useState('all');
  const [search, setSearch]         = useState('');
  // Sub-views (2026-07-12): 'invoices' = the FIN/PO crosswalk below;
  // 'cards' = monthly card-statement reconciliation
  // (CardStatementsSection). Same question — does the money agree? —
  // different book pair (bank↔Gilda beside books↔Gilda), so they share
  // the page but never a table.
  const [subView, setSubView]       = useState('invoices');
  // Sort state — click-to-sort via column headers (the legacy dropdown is
  // gone, Part B 2026-06-14). { key, dir } shape so secondary/tertiary
  // sort keys stay ascending under either direction (see
  // compareReconciliationRows). Click the active column header to toggle
  // dir; click a different column to switch key + default dir from
  // SORT_DEFAULT_DIR.
  const [sortState, setSortState] = useState({ key: 'date', dir: 'desc' });
  // Drawer state (Part B B1, 2026-06-14). `kind` selects the body
  // template; `row` is the source data row clicked. Setting to null
  // closes the drawer. ReconciliationDrawer pulls live data (items,
  // vendorPayments, receipts, paymentAllocations, etc.) from
  // useAppContext so the drawer always reflects current state, not a
  // snapshot at click time.
  const [drawer, setDrawer] = useState(null);
  function openDrawer(kind, row) { setDrawer({ kind: kind, row: row }); }
  function closeDrawer() { setDrawer(null); }
  function toggleSort(key) {
    setSortState(function(prev) {
      if (prev.key === key) {
        return { key: key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: key, dir: SORT_DEFAULT_DIR[key] || 'asc' };
    });
  }

  const rows = useMemo(function() {
    return buildReconciliationRows({
      invoices: invoices, items: items, purchaseOrders: purchaseOrders,
      vendorPayments: vendorPayments, paymentAllocations: paymentAllocations,
      proposals: proposals, clientPayments: clientPayments, projects: projects,
    });
  }, [invoices, items, purchaseOrders, vendorPayments, paymentAllocations, proposals, clientPayments, projects]);

  // Search/filter at FIN-group granularity, NOT per-row. If any row
  // within a FIN matches the search, include ALL of that FIN's rows so
  // the multi-PO context stays intact. Otherwise searching for one
  // vendor name would hide the FIN's other POs and break the picture.
  const filteredRows = useMemo(function() {
    // Dash-tolerant normalization (Cimon 2026-07-12 search report):
    // macOS/Safari can swap a typed hyphen for an en/em dash, which
    // silently breaks "26HOB-PO-009" matching. Both sides of the
    // comparison collapse every dash variant to '-' and squash runs
    // of whitespace.
    function norm(s) {
      return String(s == null ? '' : s).toLowerCase()
        .replace(/[‐-―−]/g, '-')
        .replace(/\s+/g, ' ').trim();
    }
    const q = norm(search);
    if (!q && projFilter === 'all') return rows;
    // Group key for search inclusion: FIN id for FIN-driven rows (so a
    // single match inside a multi-PO FIN keeps all its PO rows visible),
    // poId for PO-only rows (each is its own atomic entity — pooling
    // them under a shared null key would mean one match flooded in
    // every other PO-only row).
    function groupKey(r) { return r.finId || ('po:' + r.poId); }
    // Item names + SKUs on the row's PO join the haystack (same
    // 2026-07-12 report: searching an item name found nothing — items
    // were never searchable here).
    const posById = {};
    purchaseOrders.forEach(function(po) { if (po && po.id) posById[po.id] = po; });
    const itemsById = {};
    items.forEach(function(it) { if (it && it.id) itemsById[it.id] = it; });
    function itemBits(r) {
      const po = r.poId ? posById[r.poId] : null;
      return ((po && po.items) || []).map(function(iid) {
        const it = itemsById[iid];
        return it ? [it.item, it.productName, it.itemNumber].filter(Boolean).join(' ') : '';
      }).join(' ');
    }
    const matchingGroupIds = {};
    rows.forEach(function(r) {
      if (projFilter !== 'all' && r.project !== projFilter) return;
      if (!q) { matchingGroupIds[groupKey(r)] = true; return; }
      const haystack = norm([
        r.project, r.poRef, r.vendorName,
        r.proposalRef, r.finRef, r.finStatus,
        itemBits(r),
      ].join(' '));
      if (haystack.indexOf(q) >= 0) matchingGroupIds[groupKey(r)] = true;
    });
    return rows.filter(function(r) {
      if (projFilter !== 'all' && r.project !== projFilter) return false;
      return !!matchingGroupIds[groupKey(r)];
    });
  }, [rows, projFilter, search, purchaseOrders, items]);

  // Sort visible rows with the 3-key cascade (primary user-chosen key,
  // secondary finId, tertiary poRef). Pre-Part B this code grouped rows
  // by finId BEFORE sorting and used the first PO's value of the group
  // as the sort proxy — the bug shape: a multi-vendor FIN sorted by
  // "vendor" used only its first PO's vendor, hiding the rest. The new
  // model sorts rows independently; for FIN-level keys (project / date /
  // amount / status) every row of a FIN ties on primary so secondary
  // (finId) + tertiary (poRef) keep groups intact. For per-row keys
  // (vendor / po / per-PO money) rows of the same FIN may scatter; the
  // renderer's adjacency detection (see below) handles the visual
  // "first row of FIN" treatment in either case.
  const sortedRows = useMemo(function() {
    return filteredRows.slice().sort(function(a, b) {
      return compareReconciliationRows(a, b, sortState.key, sortState.dir);
    });
  }, [filteredRows, sortState]);

  // Adjacency-detected "first row of FIN" map: a row is the visual first
  // when its finId differs from the previous row's. For FIN-level sort
  // keys every multi-PO FIN's rows tie on primary → secondary (finId) →
  // tertiary (poRef) so they stay adjacent and only the lead row carries
  // FIN columns (today's behavior). For per-row sort keys (vendor / PO
  // money) rows can scatter; every scattered row becomes the "first" of
  // its run and shows the FIN columns so no information is lost.
  const firstForFinMap = useMemo(function() {
    const out = {};
    let prevFinId = null;
    sortedRows.forEach(function(r) {
      const rowKey = (r.finId || 'no-fin') + ':' + (r.poId || 'orphan');
      if (!r.finId) {
        // PO-only rows are atomic — no multi-row group to suppress
        // against. Always treat as first so the row's own project
        // shows; FIN-side columns are blank by construction and the
        // click handlers gate on r.finId below.
        out[rowKey] = true;
        prevFinId = null;
      } else {
        out[rowKey] = (r.finId !== prevFinId);
        prevFinId = r.finId;
      }
    });
    return out;
  }, [sortedRows]);

  // Totals across the visible row set. Respects projFilter + search via
  // filteredRows; sort order doesn't affect sums. Multi-PO FINs dedupe
  // by finId for client-side columns (Full Payment / Items+Fees / Markup
  // / Taxes / Client Balance); per-PO columns sum across every row.
  const totals = useMemo(function() {
    return computeReconciliationTotals(sortedRows);
  }, [sortedRows]);

  // Project list — only projects that actually appear in the row set
  // (have a FIN OR a visible PO-only row). Avoids cluttering the filter
  // pills with projects that have no reconciliation activity yet.
  const projectsInRows = useMemo(function() {
    const seen = {};
    rows.forEach(function(r) { if (r.project) seen[r.project] = true; });
    return projects
      .filter(function(pr) { return seen[pr.label]; })
      .map(function(pr) { return pr.label; })
      .sort();
  }, [rows, projects]);

  // ── Render ──────────────────────────────────────────────────────

  // Brand button geometry — bigger than column-level pills (24px).
  const projectBtn = function(active) {
    return {
      height: 32, padding: '0 14px', borderRadius: 16,
      border: active ? '1.5px solid ' + COLOR_TEXT : '0.5px solid ' + COLOR_BORDER_DK,
      background: COLOR_GROUND,
      color: active ? COLOR_TEXT : COLOR_MUTED,
      fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
      cursor: 'pointer', whiteSpace: 'nowrap',
    };
  };

  // 14 columns total at minWidth 1480px — denser than the original
  // setup, so padding tightened from 10/10 to 7/8. Larger fonts move
  // back up for the columns that need emphasis (Full Payment) via
  // inline overrides at the cell.
  // Sticky header (Cimon 2026-07-12: "column names don't scroll").
  // The scroll wrapper gets a maxHeight + overflow:auto so vertical
  // scrolling happens INSIDE the container — position:sticky can't
  // escape an overflow-x:auto ancestor, so the container must own the
  // vertical scroll for top-stickiness to work at all. Two header rows
  // stack: group row at top:0, column row at top:GROUP_HEADER_H (the
  // group row's height is pinned via box-sizing so the offset is
  // exact). Header hairline rendered as an inset box-shadow, not
  // borderBottom — collapsed-table borders detach from sticky cells in
  // Chrome and scroll away with the body.
  const GROUP_HEADER_H = 28;
  const colHeaderStyle = {
    padding: '8px 8px',
    fontSize: 9, fontWeight: 500, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: COLOR_MUTED,
    boxShadow: 'inset 0 -0.5px 0 ' + COLOR_BORDER,
    textAlign: 'left', whiteSpace: 'nowrap', verticalAlign: 'bottom',
    position: 'sticky', top: GROUP_HEADER_H, zIndex: 2, background: COLOR_GROUND,
  };
  const colHeaderRight = Object.assign({}, colHeaderStyle, { textAlign: 'right' });
  const dividerCol = { borderLeft: '0.5px solid ' + COLOR_BORDER };
  // Group header — one notch larger than colHeader, centered, no
  // bottom border (the colHeader row's border becomes the unified
  // header divider). Uppercase + letterspacing pattern preserved.
  const groupHeaderStyle = {
    padding: '8px 8px 4px',
    fontSize: 10, fontWeight: 500, letterSpacing: '0.10em',
    textTransform: 'uppercase', color: COLOR_TEXT,
    textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'bottom',
    position: 'sticky', top: 0, zIndex: 2, background: COLOR_GROUND,
    boxSizing: 'border-box', height: GROUP_HEADER_H,
  };

  const cellStyle = {
    padding: '8px 8px', fontSize: 11, color: COLOR_TEXT,
    borderBottom: '0.5px solid ' + COLOR_BORDER, verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  };
  const cellRight = Object.assign({}, cellStyle, { textAlign: 'right' });
  // Click-affordance for cells that open a drawer (B1, Part B 2026-06-14).
  // Cursor change is the primary signal; the drawer content makes the
  // affordance obvious once discovered. No hover background — keeps the
  // brand language (hairline borders, white ground, no semitransparent
  // fills) consistent with the rest of the surface.
  const clickable = { cursor: 'pointer' };

  // Sticky-column helper for the Project column. When the wide table
  // scrolls horizontally, the row's project label stays visible so the
  // bookkeeper doesn't lose row context. Background needs to be opaque
  // (white) so the underlying scrolling content doesn't bleed through.
  function stickyCol(left) {
    return {
      position: 'sticky', left: left + 'px', zIndex: 1,
      boxShadow: '1px 0 0 ' + COLOR_BORDER,
    };
  }

  const subViewPills = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      <button onClick={function() { setSubView('invoices'); }} style={projectBtn(subView === 'invoices')}>
        Invoices
      </button>
      <button onClick={function() { setSubView('cards'); }} style={projectBtn(subView === 'cards')}>
        Statements
      </button>
      <button onClick={function() { setSubView('po'); }} style={projectBtn(subView === 'po')}>
        PO Lookup
      </button>
    </div>
  );

  if (subView === 'po') {
    return (
      <div style={{ padding: '20px 18px', background: COLOR_GROUND, minHeight: '100%' }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 500, color: COLOR_TEXT }}>Reconciliation</div>
          <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 4, lineHeight: 1.5 }}>
            Search a PO — every transaction against it surfaces in one ledger: vendor payments, receipts, bank and card charges, and the client-side invoices and payments.
          </div>
        </div>
        {subViewPills}
        <PoLedgerSection />
      </div>
    );
  }

  if (subView === 'cards') {
    return (
      <div style={{ padding: '20px 18px', background: COLOR_GROUND, minHeight: '100%' }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 500, color: COLOR_TEXT }}>Reconciliation</div>
          <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 4, lineHeight: 1.5 }}>
            Monthly card and bank statements cross-checked against Gilda&rsquo;s records. Charges match to receipts and sort into buckets; incoming deposits check against recorded client payments.
          </div>
        </div>
        {subViewPills}
        <CardStatementsSection />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 18px', background: COLOR_GROUND, minHeight: '100%' }}>
      {/* Title */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: COLOR_TEXT }}>Reconciliation</div>
        <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 4, lineHeight: 1.5 }}>
          One row per PO &times; FIN pair. Issued/Confirmed/Complete POs without a FIN appear with the client-side columns blank — vendor activity surfaces immediately so initial payments can be reconciled before the FIN closes the transaction. Click any column header to sort. FIN-level columns appear once per FIN group; multi-PO FINs split when sorting by vendor or per-PO columns.
        </div>
      </div>
      {subViewPills}

      {/* Search bar */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          placeholder="Search project, PO, vendor, item, SKU, proposal, FIN ref…" autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          style={{
            width: '100%', maxWidth: 480, height: 32, padding: '0 12px',
            border: '0.5px solid ' + COLOR_BORDER_DK, borderRadius: 6,
            background: COLOR_GROUND, color: COLOR_TEXT,
            fontSize: 12, fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Project filter buttons — sort dropdown removed Part B 2026-06-14
          (replaced by click-to-sort column headers below). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <button onClick={function() { setProjFilter('all'); }} style={projectBtn(projFilter === 'all')}>
          All Projects
        </button>
        {projectsInRows.map(function(label) {
          return (
            <button key={label} onClick={function() { setProjFilter(label); }} style={projectBtn(projFilter === label)}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      {sortedRows.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center',
          color: COLOR_MUTED, fontSize: 12, fontStyle: 'italic',
          border: '0.5px dashed ' + COLOR_BORDER, borderRadius: 6 }}>
          {search.trim()
            ? ('Nothing matches "' + search.trim() + '"'
               + (projFilter !== 'all' ? (' — and the ' + projFilter + ' project filter is on, so only that project was searched') : '')
               + '.')
            : (projFilter !== 'all'
               ? ('No rows for ' + projFilter + '.')
               : 'No final invoices match the current filters.')}
          {(search.trim() || projFilter !== 'all') ? (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={function() { setSearch(''); setProjFilter('all'); }}
                style={{
                  height: 26, padding: '0 12px', borderRadius: 13,
                  border: '0.5px solid ' + COLOR_BORDER_DK,
                  background: COLOR_GROUND, color: COLOR_TEXT,
                  fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                  fontStyle: 'normal', cursor: 'pointer',
                }}>
                Clear search &amp; filters
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 230px)', border: '0.5px solid ' + COLOR_BORDER, borderRadius: 6 }}>
          {/* 14 columns. Three groups separated by hairline dividers.
              minWidth lets the table scroll horizontally on narrower
              viewports; first column (Project) is sticky so the row
              context stays visible during scroll. */}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1480, fontSize: 11 }}>
            <thead>
              {/* Group header row — Vendor side spans cols 2-5,
                  Client side spans cols 6-11. Reconciliation cols
                  (Paid Date / Status / QB) stay ungrouped. */}
              <tr>
                <th style={Object.assign({}, groupHeaderStyle, stickyCol(0), { background: COLOR_GROUND, zIndex: 3 })}></th>
                <th colSpan={4} style={Object.assign({}, groupHeaderStyle, dividerCol)}>Vendor</th>
                <th colSpan={6} style={Object.assign({}, groupHeaderStyle, dividerCol)}>Client</th>
                <th colSpan={3} style={Object.assign({}, groupHeaderStyle, dividerCol)}></th>
              </tr>
              <tr>
                <SortableTH sortKey="project" label="Project" style={Object.assign({}, colHeaderStyle, stickyCol(0), { background: COLOR_GROUND, zIndex: 3 })} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="po"      label="PO / Vendor"  style={Object.assign({}, colHeaderStyle, dividerCol)} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="po_amount" label="PO Amount" align="right" style={colHeaderRight} sortState={sortState} onClick={toggleSort} />
                <SortableTH
                  sortKey="vendor_paid"
                  label="Total Vendor Paid"
                  align="right"
                  style={colHeaderRight}
                  sortState={sortState}
                  onClick={toggleSort}
                  title="Sum of all vendor_payments principal rows linked to this PO (Deposit + Final + Full + Fee payment types). Excludes the separate credit-card processing fee rows. Multi-payment POs show the latest payment date as a subtitle under the amount."
                />
                <SortableTH sortKey="vendor_balance" label="Vendor Balance" align="right" style={colHeaderRight} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="fin"       label="FIN Ref"       style={Object.assign({}, colHeaderStyle, dividerCol)} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="amount"    label="Full Payment"  sub="PRE-TAX" align="right" style={Object.assign({}, colHeaderRight, { color: COLOR_CALC })} sortState={sortState} onClick={toggleSort}
                  title="The client price before tax — items + fees + markup. The client's actual wire is this plus Taxes. Start of the profit calc: Full Payment − Items + Fees = Markup." />
                <SortableTH sortKey="items_fees"    label="Items + Fees"   sub="COGS" align="right" style={Object.assign({}, colHeaderRight, { color: COLOR_CALC })} sortState={sortState} onClick={toggleSort}
                  title="Cost of goods sold — what the items and their fees cost before markup." />
                <SortableTH sortKey="markup"        label="Markup"         align="right" style={colHeaderRight} sortState={sortState} onClick={toggleSort}
                  title="Gross profit: Full Payment − Items + Fees. Taxes are pass-through and excluded." />
                <SortableTH sortKey="taxes"         label="Taxes"          align="right" style={colHeaderRight} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="client_balance" label="Client Balance" align="right" style={colHeaderRight} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="paid_date"  label="Paid Date" style={Object.assign({}, colHeaderStyle, dividerCol)} sortState={sortState} onClick={toggleSort} />
                <SortableTH sortKey="status"     label="Status"    style={colHeaderStyle} sortState={sortState} onClick={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(function(r) {
                const vb = vendorBalanceRender(r.vendorBalance, r.hasFinalVendorPayment);
                const rowKey = (r.finId || 'no-fin') + ':' + (r.poId || 'orphan');
                // Adjacency-detected "first row of FIN" — true when the
                // previous row's finId differs. Replaces the old
                // r.isFirstForFin field which was computed at build time
                // assuming groups always stay together (broke under
                // per-row sorts like vendor).
                const first = !!firstForFinMap[rowKey];
                // hasFin: true when this row has a FIN linkage. PO-only
                // rows (carried for visibility before the FIN is issued)
                // have finId=null and render the FIN-side columns blank.
                // Click handlers for FIN Ref + Client Balance cells gate
                // on this so they don't open empty drawers on PO-only
                // rows.
                const hasFin = !!r.finId;
                const cb = (first && hasFin) ? clientBalanceRender(r.clientBalance, r.finStatus) : null;
                const st = (first && hasFin) ? statusText(r.finStatus) : null;
                const multi = r.totalPosForFin > 1;
                return (
                  <tr key={rowKey}>
                    <td style={Object.assign({}, cellStyle, stickyCol(0), { background: COLOR_GROUND })}>{first ? (r.project || '—') : ''}</td>
                    <td
                      style={Object.assign({}, cellStyle, dividerCol, r.poId ? clickable : {})}
                      onClick={r.poId ? function() { openDrawer('po', r); } : undefined}
                      title={r.poId ? 'View PO detail' : ''}
                    >
                      <div style={{ fontSize: 11, color: COLOR_TEXT }}>{r.poRef}</div>
                      <div style={{ fontSize: 10, color: COLOR_MUTED, marginTop: 2 }}>
                        {r.vendorName}
                        {first && multi ? (
                          <span style={{ marginLeft: 6, color: COLOR_MUTED }}>
                            &middot; {r.totalPosForFin} POs total
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td style={cellRight}>{r.poAmount > 0 ? fmtMoney(r.poAmount) : '—'}</td>
                    <td
                      style={Object.assign({}, cellRight, r.poId ? clickable : {})}
                      onClick={r.poId ? function() { openDrawer('vendor-payments', r); } : undefined}
                      title={r.poId ? 'View payment history + receipt links' : ''}
                    >
                      <div>{r.actualVendorPayment > 0 ? fmtMoney(r.actualVendorPayment) : '—'}</div>
                      {r.latestVendorPaymentDate ? (
                        <div style={{ fontSize: 10, color: COLOR_MUTED, marginTop: 2 }}>
                          {String(r.latestVendorPaymentDate).slice(0, 10)}
                        </div>
                      ) : null}
                    </td>
                    <td style={Object.assign({}, cellRight, { color: vb.color })}>{vb.text}</td>
                    <td
                      style={Object.assign({}, cellStyle, dividerCol, (first && hasFin) ? clickable : {})}
                      onClick={(first && hasFin) ? function() { openDrawer('fin', r); } : undefined}
                      title={(first && hasFin) ? 'View FIN detail' : ''}
                    >
                      {first && hasFin ? r.finRef : (first ? '—' : '')}
                    </td>
                    <td style={Object.assign({}, cellRight, (first && hasFin) ? { fontWeight: 500, color: COLOR_CALC } : {})}>
                      {first && hasFin ? fmtMoney(r.finAmount) : (first ? '—' : '')}
                    </td>
                    <td style={Object.assign({}, cellRight, (first && hasFin) ? { fontWeight: 500, color: COLOR_CALC } : {})}>{first && hasFin ? fmtMoney(r.itemsAndFeesNet) : (first ? '—' : '')}</td>
                    <td style={cellRight}>{first && hasFin ? fmtMoney(r.markup) : (first ? '—' : '')}</td>
                    <td style={cellRight}>{first && hasFin ? (r.salesTax > 0 ? fmtMoney(r.salesTax) : '—') : (first ? '—' : '')}</td>
                    <td
                      style={Object.assign({}, cellRight, first && cb ? { color: cb.color } : {}, (first && hasFin) ? clickable : {})}
                      onClick={(first && hasFin) ? function() { openDrawer('client-balance', r); } : undefined}
                      title={(first && hasFin) ? 'View client payment history' : ''}
                    >
                      {first && cb ? cb.text : (first ? '—' : '')}
                    </td>
                    <td style={Object.assign({}, cellStyle, dividerCol)}>
                      {first && hasFin ? (r.paidDateForQb || '—') : (first ? '—' : '')}
                    </td>
                    <td style={Object.assign({}, cellStyle, first && st ? { color: st.color } : {})}>
                      {first && st ? st.text : (first && !hasFin ? 'No FIN' : '')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Totals bar (B4, Part B 2026-06-14). Aggregates the
                visible row set — projFilter + search already applied.
                Multi-PO FIN handling: PO-level columns sum across every
                row; FIN-level columns dedupe by finId so a 3-PO FIN
                doesn't 3x its client-side numbers. See
                computeReconciliationTotals in utils.js. */}
            <tfoot>
              <tr>
                <td style={Object.assign({}, cellStyle, stickyCol(0), {
                  background: COLOR_GROUND, fontWeight: 500, color: COLOR_TEXT,
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none',
                })}>
                  Totals
                  <div style={{ fontSize: 10, fontWeight: 400, color: COLOR_MUTED, marginTop: 2 }}>
                    {totals.finCount} FIN{totals.finCount === 1 ? '' : 's'} &middot; {totals.rowCount} row{totals.rowCount === 1 ? '' : 's'}
                  </div>
                </td>
                <td style={Object.assign({}, cellStyle, dividerCol, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none',
                })} />
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500,
                })}>{totals.poAmount > 0 ? fmtMoney(totals.poAmount) : '—'}</td>
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500,
                })}>{totals.actualVendorPayment > 0 ? fmtMoney(totals.actualVendorPayment) : '—'}</td>
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500,
                  color: totals.vendorBalance > 0.005 ? COLOR_ALERT : COLOR_MUTED,
                })}>{fmtMoney(totals.vendorBalance)}</td>
                <td style={Object.assign({}, cellStyle, dividerCol, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none',
                })} />
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500, color: COLOR_CALC,
                })}>{fmtMoney(totals.finAmount)}</td>
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500, color: COLOR_CALC,
                })}>{fmtMoney(totals.itemsAndFeesNet)}</td>
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500,
                })}>{fmtMoney(totals.markup)}</td>
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500,
                })}>{totals.salesTax > 0 ? fmtMoney(totals.salesTax) : '—'}</td>
                <td style={Object.assign({}, cellRight, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none', fontWeight: 500,
                  color: totals.clientBalance > 0.005 ? COLOR_ALERT : COLOR_MUTED,
                })}>{fmtMoney(totals.clientBalance)}</td>
                <td style={Object.assign({}, cellStyle, dividerCol, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none',
                })} />
                <td style={Object.assign({}, cellStyle, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none',
                })} />
                <td style={Object.assign({}, cellStyle, {
                  borderTop: '1px solid ' + COLOR_BORDER_DK, borderBottom: 'none',
                })} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Reconciliation drawer — opens when the bookkeeper clicks one of
          the four clickable cells. Renders nothing when drawer === null
          (the component short-circuits on !open). */}
      <ReconciliationDrawer
        open={!!drawer}
        kind={drawer && drawer.kind}
        row={drawer && drawer.row}
        onClose={closeDrawer}
      />
    </div>
  );
}
